import { useEffect } from 'react';
import { Modal, Box, Button, Icon, Card, Spinner } from '@nimbus-ds/components';
import { ExternalLinkIcon } from '@nimbus-ds/icons';
import { InteractiveList } from '@nimbus-ds/patterns';
import {
  PlansListResponseDto,
  TypeFeaturesPlansListResponseDto,
} from '@tiendanube/common';
import ModalAside from 'App/components/lab/ModalAside';
import { PATH } from 'App/components/Menu/components/utils';
import { BILLING_UPSELL_DISCOUNT } from 'App/featuresFlags';
import { useIsInAppBrowser } from 'App/hooks';
import { SubmitButton } from 'commons/components';
import goToAdmin from 'commons/utils/gotToAdmin';
import {
  useAdminLinks,
  useGetStoreInfo,
  useGoToPlans,
  useIsMobileDevice,
  useGetTags,
} from 'domains/Auth/hooks';
import usePaymentMethods from 'domains/Billing/Checkout/hooks/usePaymentMethods';
import { useChargeCreation } from 'domains/Billing/Checkout/pages/PlansAndSubscriptions/components/PaymentsTabs/components/SelectChargesToPayTab/hooks';
import FreeTrialAlert from './components/FreeTrialAlert';
import UpsellBodyCard from './components/UpsellBodyCard';
import { BILLING_ROUTES } from '../billingRoutes';
import {
  trackingBillingUpsellModalClose,
  trackingBillingUpsellPlanChange,
  trackingBillingUpsellPlansPageClick,
  trackingBillingUpsellDiscountShow,
  trackingBillingUpsellDiscountClick,
  trackingBillingUpsellDiscountClose,
} from '../tracking';
import useTranslationBilling from '../useTranslationBilling';

export interface UpsellFlowModalProps {
  show?: boolean;
  title?: string;
  featureKey: string;
  currentPlan: PlansListResponseDto;
  newPlan: PlansListResponseDto;
  ctasDisabled?: boolean;
  bulletProgress?: number;
  planChangeComplete?: boolean;
  trackingSource?: string;
  asAside?: boolean;
  confirmationCtaText?: string;
  onDismiss: () => void;
  onAccept: () => void;
  onFinalize: () => void;
  onSeePlans?: () => void;
  confirmationTextCta?: string;
  planChangeInProgress?: boolean;
}

export function UpsellFlowModal({
  show = true,
  title,
  featureKey,
  currentPlan,
  newPlan,
  ctasDisabled = false,
  bulletProgress = 0,
  planChangeComplete = false,
  trackingSource,
  asAside = false,
  onDismiss,
  onAccept,
  onFinalize,
  onSeePlans,
  confirmationTextCta,
  planChangeInProgress = false,
}: Readonly<UpsellFlowModalProps>) {
  const t = useTranslationBilling();
  const isMobileDevice = useIsMobileDevice();
  const isInAppBrowserOpen = useIsInAppBrowser();
  const { getOldAdminLink, getNewAdminLink } = useAdminLinks();
  const { isMobileNonFreemium } = useGoToPlans();
  const { isFreeDaysApplicable } = useGetStoreInfo();
  const showFinalizeCta =
    planChangeComplete && bulletProgress === newPlan.upsellBullets.length;
  const tags = useGetTags();
  const hasOnboardingDiscount = tags.includes(BILLING_UPSELL_DISCOUNT);
  const { onContinue, isLoading } = useChargeCreation();
  const { firstRecurrencyOptionAsCharge } = usePaymentMethods();

  useEffect(() => {
    if (show && hasOnboardingDiscount && showFinalizeCta) {
      trackingBillingUpsellDiscountShow();
    }
  }, [show, hasOnboardingDiscount, showFinalizeCta]);

  const currentPlanValue: TypeFeaturesPlansListResponseDto =
    currentPlan.featureKeys[featureKey];
  const newPlanValue: TypeFeaturesPlansListResponseDto =
    newPlan.featureKeys[featureKey];

  const onCloseModal = () => {
    if (planChangeComplete) {
      onFinalize();
      return;
    }

    trackingBillingUpsellModalClose({
      featureKey,
      currentPlan: currentPlan.name,
      newPlan: newPlan.name,
      currentPlanValue,
      newPlanValue,
      source: trackingSource,
    });
    onDismiss();
  };

  const onClickAccept = () => {
    trackingBillingUpsellPlanChange({
      featureKey,
      currentPlan: currentPlan.name,
      newPlan: newPlan.name,
      currentPlanValue,
      newPlanValue,
      source: trackingSource,
    });
    onAccept();
  };

  const goToPlansPage = () => {
    trackingBillingUpsellPlansPageClick({
      featureKey,
      currentPlan: currentPlan.name,
      newPlan: newPlan.name,
      currentPlanValue,
      newPlanValue,
      source: trackingSource,
    });
    goToAdmin(PATH.plans)();
  };

  const onDiscountClick = async () => {
    trackingBillingUpsellDiscountClick();
    if (firstRecurrencyOptionAsCharge && !isLoading) {
      await onContinue([firstRecurrencyOptionAsCharge]);
    }
    onDismiss();
  };

  const onDiscountCancel = () => {
    trackingBillingUpsellDiscountClose();
    onCloseModal();
  };

  const getTitle = () => {
    if (planChangeComplete) {
      return t('upsellFlow.modal.titleChangeComplete');
    }
    if (planChangeInProgress) {
      return t('upsellFlow.modal.titleChangeInProgress');
    }
    return title || t('upsellFlow.modal.title');
  };

  const modalBody = (
    <UpsellBodyCard
      newPlan={newPlan}
      currentPlan={currentPlan}
      bulletProgress={bulletProgress}
      planChangeInProgress={planChangeInProgress}
      goToPlansPage={goToPlansPage}
      planChangeComplete={!planChangeInProgress && planChangeComplete}
      hideBullets={hasOnboardingDiscount && showFinalizeCta}
    />
  );

  let modalCtas;
  if (hasOnboardingDiscount && showFinalizeCta) {
    modalCtas = (
      <Card padding="none">
        <Box overflow="hidden" borderRadius="2">
          <InteractiveList bottomDivider={false}>
            <InteractiveList.ButtonItem
              title={t('upsellFlow.modal.ctaDiscountTitle')}
              description={t('upsellFlow.modal.ctaDiscountDescription')}
              iconButton={{
                onClick: onDiscountClick,
                disabled: isLoading,
              }}
              topDivider={false}
            >
              {isLoading && (
                <Box display="flex" justifyContent="center" paddingTop="2">
                  <Spinner color="currentColor" size="small" />
                </Box>
              )}
            </InteractiveList.ButtonItem>
            <InteractiveList.ButtonItem
              title={t('upsellFlow.modal.ctaDiscountCancel')}
              iconButton={{ onClick: onDiscountCancel }}
              topDivider
            />
          </InteractiveList>
        </Box>
      </Card>
    );
  } else if (hasOnboardingDiscount && planChangeInProgress) {
    modalCtas = null;
  } else if (showFinalizeCta || planChangeInProgress) {
    modalCtas = (
      <SubmitButton
        appearance="primary"
        onClick={onFinalize}
        isLoading={planChangeInProgress}
      >
        {confirmationTextCta || t('upsellFlow.confimration.cta')}
      </SubmitButton>
    );
  } else {
    modalCtas = (
      <>
        {!planChangeInProgress && (
          <Button onClick={onCloseModal} disabled={ctasDisabled}>
            {t('upsellFlow.modal.ctaCancel')}
          </Button>
        )}
        {isMobileDevice || isInAppBrowserOpen ? (
          <Button
            appearance="primary"
            as="a"
            onClick={() => onSeePlans?.()}
            href={
              isMobileNonFreemium
                ? getOldAdminLink(BILLING_ROUTES.plans)
                : getNewAdminLink(BILLING_ROUTES.plans)
            }
            target="_blank"
          >
            {t('upsellFlow.modal.seePlans')}{' '}
            <Icon color="currentColor" source={<ExternalLinkIcon />} />
          </Button>
        ) : (
          <SubmitButton onClick={onClickAccept} isLoading={ctasDisabled}>
            {t(
              `upsellFlow.modal.${
                isFreeDaysApplicable ? 'ctaAccept' : 'ctaAcceptPay'
              }`,
            )}
          </SubmitButton>
        )}
      </>
    );
  }

  const alert = isFreeDaysApplicable ? <FreeTrialAlert /> : null;

  return asAside ? (
    <ModalAside isOpen={show} headerContent={getTitle()}>
      {alert}
      <Box>{modalBody}</Box>
      <Box display="flex" gap="2" justifyContent="flex-end">
        {modalCtas}
      </Box>
    </ModalAside>
  ) : (
    <Modal open={show}>
      <Modal.Header title={getTitle()} />
      <Modal.Body padding="none">{alert}</Modal.Body>
      <Modal.Body padding="none">{modalBody}</Modal.Body>
      <Modal.Footer>{modalCtas}</Modal.Footer>
    </Modal>
  );
}
